// Kernel Domain — Mobile bottom-nav resolution. A cross-feature invariant (which pages are the
// "primary" bottom-tab destinations for a given role/permission set) that many features and the
// app shell both need to agree on, so it lives here rather than in any one feature.

/** A capability gate: present only when the destination requires specific permissions/a feature flag. */
export interface MobileNavGate {
  permissions?: readonly string[];
  feature?: string;
}

export interface MobileNavCandidate extends MobileNavGate {
  path: string;
}

/** The minimal shape this module needs from clinic-access's `ClinicAccessResponse` — duck-typed so
 * kernel never imports a feature. */
export interface MobileNavAccess {
  permissions: readonly string[];
  enabledFeatures: readonly string[];
}

function isGranted(access: MobileNavAccess | undefined, gate: MobileNavGate): boolean {
  if (!gate.permissions) return true;
  if (!access) return false;
  const hasPermission = gate.permissions.some((permission) => access.permissions.includes(permission));
  return hasPermission && (!gate.feature || access.enabledFeatures.includes(gate.feature));
}

/**
 * The full, priority-ordered list of mobile destinations that can appear in the bottom tab bar or
 * the "More" list — ranked by daily-use frequency (see PRODUCT.md's per-role descriptions), not
 * alphabetically. Front-desk roles (owner/receptionist) get the requests inbox as their bottom-tab
 * slot instead of the calendar — but the calendar is still a distinct page they need sometimes, so
 * it stays reachable too, just lower priority (right after the fixed Queue/Patients tabs) rather
 * than competing for a bottom-tab slot.
 */
export function mobileNavCandidates(isFrontDesk: boolean): MobileNavCandidate[] {
  return [
    isFrontDesk
      ? { path: '/appointments/requests', permissions: ['queue.view'] }
      : { path: '/appointments', permissions: ['queue.view'] },
    { path: '/queue', permissions: ['queue.view'] },
    { path: '/patients', permissions: ['patients.view'] },
    ...(isFrontDesk ? [{ path: '/appointments', permissions: ['queue.view'] }] : []),
    { path: '/prescriptions', permissions: ['prescriptions.print'] },
    { path: '/billing', permissions: ['billing.invoices.view'], feature: 'billing' },
    { path: '/reports', permissions: ['analytics.clinic.view', 'analytics.own.view', 'analytics.limited.view'], feature: 'reportsLevel' },
    { path: '/staff', permissions: ['staff.manage', 'staff.invite'], feature: 'staffManagement' },
    { path: '/roles', permissions: ['staff.roles.manage'], feature: 'customRoles' },
    { path: '/subscription', permissions: ['clinic.settings.manage'] },
    { path: '/audit', permissions: ['clinic.audit.view'] },
    {
      path: '/settings',
      permissions: [
        'clinic.settings.manage',
        'clinic.services.manage',
        'clinic.rooms.manage',
        'clinic.availability.manage',
        'clinic.availability.own',
      ],
    },
    { path: '/account/settings' },
  ];
}

export interface ResolvedMobileNav {
  /** The paths promoted into the fixed-size bottom bar, in priority order (excludes "/dashboard"
   * and "/mobile/more", which the caller always renders in the first and last slots). */
  bottomTabPaths: string[];
  /** Every remaining reachable destination, in priority order — the "More" list. */
  moreListPaths: string[];
}

/**
 * Resolves which destinations fill the bottom bar's fixed middle slots vs. which spill into "More",
 * for the current role's permission set. When a role lacks permission for a higher-priority
 * destination, the next reachable one is promoted to fill its slot instead of leaving it empty or
 * showing a dead-end tab — "smart" tabs, matching what the user can actually do.
 */
export function resolveMobileNav(
  isFrontDesk: boolean,
  access: MobileNavAccess | undefined,
  middleSlotCapacity: number,
): ResolvedMobileNav {
  const reachable = mobileNavCandidates(isFrontDesk)
    .filter((candidate) => isGranted(access, candidate))
    .map((candidate) => candidate.path);

  return {
    bottomTabPaths: reachable.slice(0, middleSlotCapacity),
    moreListPaths: reachable.slice(middleSlotCapacity),
  };
}
